import { create } from "zustand";
import { generateCSSVariables, injectCSSVariables } from "@/utils/cssVariables";
import {
  upsertVariable,
  deleteVariable,
  newId,
} from "@/services/variablesService";
import { isElectron } from "@/services/storage/LocalFileSystemClient";

export interface ColorVariable {
  id: string;
  name: string;
  value: string; // hex color
}

export interface SizeVariable {
  id: string;
  name: string;
  value: string; // e.g., "20px", "50%"
}

export interface AngleVariable {
  id: string;
  name: string;
  value: number; // e.g., 45
}

export interface FontFamilyVariable {
  id: string;
  name: string;
  value: string; // font family name or stack
  src?: string; // data URL or external URL for @font-face
  format?: string; // woff2 | woff | truetype | opentype
}

export interface IconVariable {
  id: string;
  name: string;
  value: string; // icon URL, data URI, or asset reference
}

export interface ProjectVariables {
  colors: ColorVariable[];
  sizes: SizeVariable[];
  angles: AngleVariable[];
  fontFamilies: FontFamilyVariable[];
  icons: IconVariable[];
}

export interface CraftspaceVariables {
  colors: ColorVariable[];
  sizes: SizeVariable[];
  angles: AngleVariable[];
  fontFamilies: FontFamilyVariable[];
  icons: IconVariable[];
}

interface VariablesState {
  // Variables by project ID
  projectVariables: Record<string, ProjectVariables>;
  // Variables by craftspace ID
  craftspaceVariables: Record<string, CraftspaceVariables>;

  // Project Actions (craftspaceId required for SQLite persistence)
  setProjectVariables: (projectId: string, variables: ProjectVariables) => void;
  addColorVariable: (craftspaceId: string, projectId: string, variable: Omit<ColorVariable, "id">) => void;
  updateColorVariable: (
    craftspaceId: string,
    projectId: string,
    id: string,
    updates: Partial<Omit<ColorVariable, "id">>,
  ) => void;
  deleteColorVariable: (craftspaceId: string, projectId: string, id: string) => void;

  addSizeVariable: (craftspaceId: string, projectId: string, variable: Omit<SizeVariable, "id">) => void;
  updateSizeVariable: (
    craftspaceId: string,
    projectId: string,
    id: string,
    updates: Partial<Omit<SizeVariable, "id">>,
  ) => void;
  deleteSizeVariable: (craftspaceId: string, projectId: string, id: string) => void;

  addAngleVariable: (craftspaceId: string, projectId: string, variable: Omit<AngleVariable, "id">) => void;
  updateAngleVariable: (
    craftspaceId: string,
    projectId: string,
    id: string,
    updates: Partial<Omit<AngleVariable, "id">>,
  ) => void;
  deleteAngleVariable: (craftspaceId: string, projectId: string, id: string) => void;

  addFontFamilyVariable: (craftspaceId: string, projectId: string, variable: Omit<FontFamilyVariable, "id">) => void;
  updateFontFamilyVariable: (
    craftspaceId: string,
    projectId: string,
    id: string,
    updates: Partial<Omit<FontFamilyVariable, "id">>,
  ) => void;
  deleteFontFamilyVariable: (craftspaceId: string, projectId: string, id: string) => void;

  addIconVariable: (craftspaceId: string, projectId: string, variable: Omit<IconVariable, "id">) => void;
  updateIconVariable: (
    craftspaceId: string,
    projectId: string,
    id: string,
    updates: Partial<Omit<IconVariable, "id">>,
  ) => void;
  deleteIconVariable: (craftspaceId: string, projectId: string, id: string) => void;

  getProjectVariables: (projectId: string) => ProjectVariables;
  getColorVariable: (projectId: string, id: string) => ColorVariable | undefined;
  getSizeVariable: (projectId: string, id: string) => SizeVariable | undefined;
  getAngleVariable: (projectId: string, id: string) => AngleVariable | undefined;
  getFontFamilyVariable: (projectId: string, id: string) => FontFamilyVariable | undefined;
  getIconVariable: (projectId: string, id: string) => IconVariable | undefined;
  updateCSSVariables: (projectId: string) => void;

  // Craftspace Actions
  setCraftspaceVariables: (craftspaceId: string, variables: CraftspaceVariables) => void;
  addCraftspaceColorVariable: (craftspaceId: string, variable: Omit<ColorVariable, "id">) => void;
  updateCraftspaceColorVariable: (
    craftspaceId: string,
    id: string,
    updates: Partial<Omit<ColorVariable, "id">>,
  ) => void;
  deleteCraftspaceColorVariable: (craftspaceId: string, id: string) => void;

  addCraftspaceSizeVariable: (craftspaceId: string, variable: Omit<SizeVariable, "id">) => void;
  updateCraftspaceSizeVariable: (
    craftspaceId: string,
    id: string,
    updates: Partial<Omit<SizeVariable, "id">>,
  ) => void;
  deleteCraftspaceSizeVariable: (craftspaceId: string, id: string) => void;

  addCraftspaceAngleVariable: (craftspaceId: string, variable: Omit<AngleVariable, "id">) => void;
  updateCraftspaceAngleVariable: (
    craftspaceId: string,
    id: string,
    updates: Partial<Omit<AngleVariable, "id">>,
  ) => void;
  deleteCraftspaceAngleVariable: (craftspaceId: string, id: string) => void;

  addCraftspaceFontFamilyVariable: (
    craftspaceId: string,
    variable: Omit<FontFamilyVariable, "id">,
  ) => void;
  updateCraftspaceFontFamilyVariable: (
    craftspaceId: string,
    id: string,
    updates: Partial<Omit<FontFamilyVariable, "id">>,
  ) => void;
  deleteCraftspaceFontFamilyVariable: (craftspaceId: string, id: string) => void;

  addCraftspaceIconVariable: (craftspaceId: string, variable: Omit<IconVariable, "id">) => void;
  updateCraftspaceIconVariable: (
    craftspaceId: string,
    id: string,
    updates: Partial<Omit<IconVariable, "id">>,
  ) => void;
  deleteCraftspaceIconVariable: (craftspaceId: string, id: string) => void;

  getCraftspaceVariables: (craftspaceId: string) => CraftspaceVariables;
  getCraftspaceColorVariable: (craftspaceId: string, id: string) => ColorVariable | undefined;
  getCraftspaceSizeVariable: (craftspaceId: string, id: string) => SizeVariable | undefined;
  getCraftspaceAngleVariable: (craftspaceId: string, id: string) => AngleVariable | undefined;
  getCraftspaceFontFamilyVariable: (
    craftspaceId: string,
    id: string,
  ) => FontFamilyVariable | undefined;
  getCraftspaceIconVariable: (craftspaceId: string, id: string) => IconVariable | undefined;

  // Combined getters (craftspace + project variables)
  getCombinedVariables: (craftspaceId: string, projectId: string) => ProjectVariables;
  updateCombinedCSSVariables: (craftspaceId: string, projectId: string) => void;
}

const defaultProjectVariables: ProjectVariables = {
  colors: [],
  sizes: [],
  angles: [],
  fontFamilies: [],
  icons: [],
};

const defaultCraftspaceVariables: CraftspaceVariables = {
  colors: [],
  sizes: [],
  angles: [],
  fontFamilies: [],
  icons: [],
};

const updateCSSVariablesForProject = (projectId: string, projectVars: ProjectVariables) => {
  setTimeout(() => {
    const cssVariables = generateCSSVariables(
      projectVars.colors,
      projectVars.sizes,
      projectVars.fontFamilies,
    );
    injectCSSVariables(cssVariables, projectId);
  }, 0);
};

export const useVariablesStore = create<VariablesState>()((set, get) => ({
  projectVariables: {},
  craftspaceVariables: {},

  setProjectVariables: (projectId: string, variables: ProjectVariables) =>
    set((state) => ({
      projectVariables: { ...state.projectVariables, [projectId]: variables },
    })),

  addColorVariable: (craftspaceId: string, projectId: string, variable: Omit<ColorVariable, "id">) =>
    set((state) => {
      const id = isElectron() ? newId() : `temp-${newId()}`;
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = { ...projectVars, colors: [...projectVars.colors, { ...variable, id }] };
      updateCSSVariablesForProject(projectId, updated);
      if (isElectron()) {
        void upsertVariable(craftspaceId, id, projectId, "color", variable.name, variable.value);
      } else {
        void upsertVariable(craftspaceId, id, projectId, "color", variable.name, variable.value).then(
          (resolvedId) => {
            if (!resolvedId || resolvedId === id) return;
            set((current) => {
              const nextVars = current.projectVariables[projectId] ?? defaultProjectVariables;
              return {
                projectVariables: {
                  ...current.projectVariables,
                  [projectId]: {
                    ...nextVars,
                    colors: nextVars.colors.map((c) => (c.id === id ? { ...c, id: resolvedId } : c)),
                  },
                },
              };
            });
          },
        );
      }
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  updateColorVariable: (craftspaceId: string, projectId: string, id: string, updates: Partial<Omit<ColorVariable, "id">>) =>
    set((state) => {
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = {
        ...projectVars,
        colors: projectVars.colors.map((c) => (c.id === id ? { ...c, ...updates } : c)),
      };
      const color = updated.colors.find((c) => c.id === id);
      if (color) void upsertVariable(craftspaceId, id, projectId, "color", color.name, color.value);
      updateCSSVariablesForProject(projectId, updated);
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  deleteColorVariable: (craftspaceId: string, projectId: string, id: string) =>
    set((state) => {
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = { ...projectVars, colors: projectVars.colors.filter((c) => c.id !== id) };
      void deleteVariable(craftspaceId, id);
      updateCSSVariablesForProject(projectId, updated);
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  addSizeVariable: (craftspaceId: string, projectId: string, variable: Omit<SizeVariable, "id">) =>
    set((state) => {
      const id = isElectron() ? newId() : `temp-${newId()}`;
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = { ...projectVars, sizes: [...projectVars.sizes, { ...variable, id }] };
      updateCSSVariablesForProject(projectId, updated);
      if (isElectron()) {
        void upsertVariable(craftspaceId, id, projectId, "size", variable.name, variable.value);
      } else {
        void upsertVariable(craftspaceId, id, projectId, "size", variable.name, variable.value).then(
          (resolvedId) => {
            if (!resolvedId || resolvedId === id) return;
            set((current) => {
              const nextVars = current.projectVariables[projectId] ?? defaultProjectVariables;
              return {
                projectVariables: {
                  ...current.projectVariables,
                  [projectId]: {
                    ...nextVars,
                    sizes: nextVars.sizes.map((s) => (s.id === id ? { ...s, id: resolvedId } : s)),
                  },
                },
              };
            });
          },
        );
      }
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  updateSizeVariable: (craftspaceId: string, projectId: string, id: string, updates: Partial<Omit<SizeVariable, "id">>) =>
    set((state) => {
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = {
        ...projectVars,
        sizes: projectVars.sizes.map((s) => (s.id === id ? { ...s, ...updates } : s)),
      };
      const size = updated.sizes.find((s) => s.id === id);
      if (size) void upsertVariable(craftspaceId, id, projectId, "size", size.name, size.value);
      updateCSSVariablesForProject(projectId, updated);
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  deleteSizeVariable: (craftspaceId: string, projectId: string, id: string) =>
    set((state) => {
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = { ...projectVars, sizes: projectVars.sizes.filter((s) => s.id !== id) };
      void deleteVariable(craftspaceId, id);
      updateCSSVariablesForProject(projectId, updated);
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  addAngleVariable: (craftspaceId: string, projectId: string, variable: Omit<AngleVariable, "id">) =>
    set((state) => {
      const id = isElectron() ? newId() : `temp-${newId()}`;
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const angles = projectVars.angles ?? [];
      const updated = { ...projectVars, angles: [...angles, { ...variable, id }] };
      if (isElectron()) {
        void upsertVariable(craftspaceId, id, projectId, "angle", variable.name, String(variable.value));
      } else {
        void upsertVariable(craftspaceId, id, projectId, "angle", variable.name, String(variable.value)).then(
          (resolvedId) => {
            if (!resolvedId || resolvedId === id) return;
            set((current) => {
              const nextVars = current.projectVariables[projectId] ?? defaultProjectVariables;
              const nextAngles = nextVars.angles ?? [];
              return {
                projectVariables: {
                  ...current.projectVariables,
                  [projectId]: {
                    ...nextVars,
                    angles: nextAngles.map((a) => (a.id === id ? { ...a, id: resolvedId } : a)),
                  },
                },
              };
            });
          },
        );
      }
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  updateAngleVariable: (craftspaceId: string, projectId: string, id: string, updates: Partial<Omit<AngleVariable, "id">>) =>
    set((state) => {
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const angles = projectVars.angles ?? [];
      const updated = {
        ...projectVars,
        angles: angles.map((a) => (a.id === id ? { ...a, ...updates } : a)),
      };
      const angle = updated.angles.find((a) => a.id === id);
      if (angle) void upsertVariable(craftspaceId, id, projectId, "angle", angle.name, String(angle.value));
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  deleteAngleVariable: (craftspaceId: string, projectId: string, id: string) =>
    set((state) => {
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const angles = projectVars.angles ?? [];
      const updated = { ...projectVars, angles: angles.filter((a) => a.id !== id) };
      void deleteVariable(craftspaceId, id);
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  addFontFamilyVariable: (craftspaceId: string, projectId: string, variable: Omit<FontFamilyVariable, "id">) =>
    set((state) => {
      const id = isElectron() ? newId() : `temp-${newId()}`;
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = {
        ...projectVars,
        fontFamilies: [...projectVars.fontFamilies, { ...variable, id }],
      };
      updateCSSVariablesForProject(projectId, updated);
      const meta = variable.src || variable.format ? { src: variable.src, format: variable.format } : undefined;
      if (isElectron()) {
        void upsertVariable(craftspaceId, id, projectId, "font_family", variable.name, variable.value, meta);
      } else {
        void upsertVariable(craftspaceId, id, projectId, "font_family", variable.name, variable.value, meta).then(
          (resolvedId) => {
            if (!resolvedId || resolvedId === id) return;
            set((current) => {
              const nextVars = current.projectVariables[projectId] ?? defaultProjectVariables;
              return {
                projectVariables: {
                  ...current.projectVariables,
                  [projectId]: {
                    ...nextVars,
                    fontFamilies: nextVars.fontFamilies.map((f) =>
                      f.id === id ? { ...f, id: resolvedId } : f,
                    ),
                  },
                },
              };
            });
          },
        );
      }
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  updateFontFamilyVariable: (craftspaceId: string, projectId: string, id: string, updates: Partial<Omit<FontFamilyVariable, "id">>) =>
    set((state) => {
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = {
        ...projectVars,
        fontFamilies: projectVars.fontFamilies.map((f) => (f.id === id ? { ...f, ...updates } : f)),
      };
      const font = updated.fontFamilies.find((f) => f.id === id);
      if (font) {
        const meta = font.src || font.format ? { src: font.src, format: font.format } : undefined;
        void upsertVariable(craftspaceId, id, projectId, "font_family", font.name, font.value, meta);
      }
      updateCSSVariablesForProject(projectId, updated);
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  deleteFontFamilyVariable: (craftspaceId: string, projectId: string, id: string) =>
    set((state) => {
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = {
        ...projectVars,
        fontFamilies: projectVars.fontFamilies.filter((f) => f.id !== id),
      };
      void deleteVariable(craftspaceId, id);
      updateCSSVariablesForProject(projectId, updated);
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  addIconVariable: (craftspaceId: string, projectId: string, variable: Omit<IconVariable, "id">) =>
    set((state) => {
      const id = isElectron() ? newId() : `temp-${newId()}`;
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = { ...projectVars, icons: [...projectVars.icons, { ...variable, id }] };
      if (isElectron()) {
        void upsertVariable(craftspaceId, id, projectId, "icon", variable.name, variable.value);
      } else {
        void upsertVariable(craftspaceId, id, projectId, "icon", variable.name, variable.value).then(
          (resolvedId) => {
            if (!resolvedId || resolvedId === id) return;
            set((current) => {
              const nextVars = current.projectVariables[projectId] ?? defaultProjectVariables;
              return {
                projectVariables: {
                  ...current.projectVariables,
                  [projectId]: {
                    ...nextVars,
                    icons: nextVars.icons.map((i) => (i.id === id ? { ...i, id: resolvedId } : i)),
                  },
                },
              };
            });
          },
        );
      }
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  updateIconVariable: (craftspaceId: string, projectId: string, id: string, updates: Partial<Omit<IconVariable, "id">>) =>
    set((state) => {
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = {
        ...projectVars,
        icons: projectVars.icons.map((i) => (i.id === id ? { ...i, ...updates } : i)),
      };
      const icon = updated.icons.find((i) => i.id === id);
      if (icon) void upsertVariable(craftspaceId, id, projectId, "icon", icon.name, icon.value);
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  deleteIconVariable: (craftspaceId: string, projectId: string, id: string) =>
    set((state) => {
      const projectVars = state.projectVariables[projectId] ?? defaultProjectVariables;
      const updated = { ...projectVars, icons: projectVars.icons.filter((i) => i.id !== id) };
      void deleteVariable(craftspaceId, id);
      return { projectVariables: { ...state.projectVariables, [projectId]: updated } };
    }),

  getProjectVariables: (projectId: string) => {
    const vars = get().projectVariables[projectId] ?? defaultProjectVariables;
    return { ...vars, angles: vars.angles ?? [] };
  },

  getColorVariable: (projectId: string, id: string) =>
    (get().projectVariables[projectId] ?? defaultProjectVariables).colors.find((c) => c.id === id),

  getSizeVariable: (projectId: string, id: string) =>
    (get().projectVariables[projectId] ?? defaultProjectVariables).sizes.find((s) => s.id === id),

  getAngleVariable: (projectId: string, id: string) =>
    (get().projectVariables[projectId] ?? defaultProjectVariables).angles.find((a) => a.id === id),

  getFontFamilyVariable: (projectId: string, id: string) =>
    (get().projectVariables[projectId] ?? defaultProjectVariables).fontFamilies.find((f) => f.id === id),

  getIconVariable: (projectId: string, id: string) =>
    (get().projectVariables[projectId] ?? defaultProjectVariables).icons.find((i) => i.id === id),

  updateCSSVariables: (projectId: string) => {
    const projectVars = get().projectVariables[projectId] ?? defaultProjectVariables;
    const cssVariables = generateCSSVariables(
      projectVars.colors,
      projectVars.sizes,
      projectVars.fontFamilies,
    );
    injectCSSVariables(cssVariables, projectId);
  },

  // ─── Craftspace Actions ────────────────────────────────────────────────────

  setCraftspaceVariables: (craftspaceId: string, variables: CraftspaceVariables) =>
    set((state) => ({
      craftspaceVariables: { ...state.craftspaceVariables, [craftspaceId]: variables },
    })),

  addCraftspaceColorVariable: (craftspaceId: string, variable: Omit<ColorVariable, "id">) =>
    set((state) => {
      const id = isElectron() ? newId() : `temp-${newId()}`;
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      if (isElectron()) {
        void upsertVariable(craftspaceId, id, null, "color", variable.name, variable.value);
      } else {
        void upsertVariable(craftspaceId, id, null, "color", variable.name, variable.value).then(
          (resolvedId) => {
            if (!resolvedId || resolvedId === id) return;
            set((current) => {
              const nextVars = current.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
              return {
                craftspaceVariables: {
                  ...current.craftspaceVariables,
                  [craftspaceId]: {
                    ...nextVars,
                    colors: nextVars.colors.map((c) => (c.id === id ? { ...c, id: resolvedId } : c)),
                  },
                },
              };
            });
          },
        );
      }
      return {
        craftspaceVariables: {
          ...state.craftspaceVariables,
          [craftspaceId]: { ...wsVars, colors: [...wsVars.colors, { ...variable, id }] },
        },
      };
    }),

  updateCraftspaceColorVariable: (craftspaceId: string, id: string, updates: Partial<Omit<ColorVariable, "id">>) =>
    set((state) => {
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      const updated = { ...wsVars, colors: wsVars.colors.map((c) => (c.id === id ? { ...c, ...updates } : c)) };
      const color = updated.colors.find((c) => c.id === id);
      if (color) void upsertVariable(craftspaceId, id, null, "color", color.name, color.value);
      return { craftspaceVariables: { ...state.craftspaceVariables, [craftspaceId]: updated } };
    }),

  deleteCraftspaceColorVariable: (craftspaceId: string, id: string) =>
    set((state) => {
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      void deleteVariable(craftspaceId, id);
      return {
        craftspaceVariables: {
          ...state.craftspaceVariables,
          [craftspaceId]: { ...wsVars, colors: wsVars.colors.filter((c) => c.id !== id) },
        },
      };
    }),

  addCraftspaceSizeVariable: (craftspaceId: string, variable: Omit<SizeVariable, "id">) =>
    set((state) => {
      const id = isElectron() ? newId() : `temp-${newId()}`;
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      if (isElectron()) {
        void upsertVariable(craftspaceId, id, null, "size", variable.name, variable.value);
      } else {
        void upsertVariable(craftspaceId, id, null, "size", variable.name, variable.value).then(
          (resolvedId) => {
            if (!resolvedId || resolvedId === id) return;
            set((current) => {
              const nextVars = current.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
              return {
                craftspaceVariables: {
                  ...current.craftspaceVariables,
                  [craftspaceId]: {
                    ...nextVars,
                    sizes: nextVars.sizes.map((s) => (s.id === id ? { ...s, id: resolvedId } : s)),
                  },
                },
              };
            });
          },
        );
      }
      return {
        craftspaceVariables: {
          ...state.craftspaceVariables,
          [craftspaceId]: { ...wsVars, sizes: [...wsVars.sizes, { ...variable, id }] },
        },
      };
    }),

  updateCraftspaceSizeVariable: (craftspaceId: string, id: string, updates: Partial<Omit<SizeVariable, "id">>) =>
    set((state) => {
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      const updated = { ...wsVars, sizes: wsVars.sizes.map((s) => (s.id === id ? { ...s, ...updates } : s)) };
      const size = updated.sizes.find((s) => s.id === id);
      if (size) void upsertVariable(craftspaceId, id, null, "size", size.name, size.value);
      return { craftspaceVariables: { ...state.craftspaceVariables, [craftspaceId]: updated } };
    }),

  deleteCraftspaceSizeVariable: (craftspaceId: string, id: string) =>
    set((state) => {
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      void deleteVariable(craftspaceId, id);
      return {
        craftspaceVariables: {
          ...state.craftspaceVariables,
          [craftspaceId]: { ...wsVars, sizes: wsVars.sizes.filter((s) => s.id !== id) },
        },
      };
    }),

  addCraftspaceAngleVariable: (craftspaceId: string, variable: Omit<AngleVariable, "id">) =>
    set((state) => {
      const id = isElectron() ? newId() : `temp-${newId()}`;
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      const angles = wsVars.angles ?? [];
      if (isElectron()) {
        void upsertVariable(craftspaceId, id, null, "angle", variable.name, String(variable.value));
      } else {
        void upsertVariable(craftspaceId, id, null, "angle", variable.name, String(variable.value)).then(
          (resolvedId) => {
            if (!resolvedId || resolvedId === id) return;
            set((current) => {
              const nextVars = current.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
              const nextAngles = nextVars.angles ?? [];
              return {
                craftspaceVariables: {
                  ...current.craftspaceVariables,
                  [craftspaceId]: {
                    ...nextVars,
                    angles: nextAngles.map((a) => (a.id === id ? { ...a, id: resolvedId } : a)),
                  },
                },
              };
            });
          },
        );
      }
      return {
        craftspaceVariables: {
          ...state.craftspaceVariables,
          [craftspaceId]: { ...wsVars, angles: [...angles, { ...variable, id }] },
        },
      };
    }),

  updateCraftspaceAngleVariable: (craftspaceId: string, id: string, updates: Partial<Omit<AngleVariable, "id">>) =>
    set((state) => {
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      const angles = wsVars.angles ?? [];
      const updated = { ...wsVars, angles: angles.map((a) => (a.id === id ? { ...a, ...updates } : a)) };
      const angle = updated.angles.find((a) => a.id === id);
      if (angle) void upsertVariable(craftspaceId, id, null, "angle", angle.name, String(angle.value));
      return { craftspaceVariables: { ...state.craftspaceVariables, [craftspaceId]: updated } };
    }),

  deleteCraftspaceAngleVariable: (craftspaceId: string, id: string) =>
    set((state) => {
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      const angles = wsVars.angles ?? [];
      void deleteVariable(craftspaceId, id);
      return {
        craftspaceVariables: {
          ...state.craftspaceVariables,
          [craftspaceId]: { ...wsVars, angles: angles.filter((a) => a.id !== id) },
        },
      };
    }),

  addCraftspaceFontFamilyVariable: (craftspaceId: string, variable: Omit<FontFamilyVariable, "id">) =>
    set((state) => {
      const id = isElectron() ? newId() : `temp-${newId()}`;
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      const meta = variable.src || variable.format ? { src: variable.src, format: variable.format } : undefined;
      if (isElectron()) {
        void upsertVariable(craftspaceId, id, null, "font_family", variable.name, variable.value, meta);
      } else {
        void upsertVariable(craftspaceId, id, null, "font_family", variable.name, variable.value, meta).then(
          (resolvedId) => {
            if (!resolvedId || resolvedId === id) return;
            set((current) => {
              const nextVars = current.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
              return {
                craftspaceVariables: {
                  ...current.craftspaceVariables,
                  [craftspaceId]: {
                    ...nextVars,
                    fontFamilies: nextVars.fontFamilies.map((f) =>
                      f.id === id ? { ...f, id: resolvedId } : f,
                    ),
                  },
                },
              };
            });
          },
        );
      }
      return {
        craftspaceVariables: {
          ...state.craftspaceVariables,
          [craftspaceId]: { ...wsVars, fontFamilies: [...wsVars.fontFamilies, { ...variable, id }] },
        },
      };
    }),

  updateCraftspaceFontFamilyVariable: (craftspaceId: string, id: string, updates: Partial<Omit<FontFamilyVariable, "id">>) =>
    set((state) => {
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      const updated = {
        ...wsVars,
        fontFamilies: wsVars.fontFamilies.map((f) => (f.id === id ? { ...f, ...updates } : f)),
      };
      const font = updated.fontFamilies.find((f) => f.id === id);
      if (font) {
        const meta = font.src || font.format ? { src: font.src, format: font.format } : undefined;
        void upsertVariable(craftspaceId, id, null, "font_family", font.name, font.value, meta);
      }
      return { craftspaceVariables: { ...state.craftspaceVariables, [craftspaceId]: updated } };
    }),

  deleteCraftspaceFontFamilyVariable: (craftspaceId: string, id: string) =>
    set((state) => {
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      void deleteVariable(craftspaceId, id);
      return {
        craftspaceVariables: {
          ...state.craftspaceVariables,
          [craftspaceId]: {
            ...wsVars,
            fontFamilies: wsVars.fontFamilies.filter((f) => f.id !== id),
          },
        },
      };
    }),

  addCraftspaceIconVariable: (craftspaceId: string, variable: Omit<IconVariable, "id">) =>
    set((state) => {
      const id = isElectron() ? newId() : `temp-${newId()}`;
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      if (isElectron()) {
        void upsertVariable(craftspaceId, id, null, "icon", variable.name, variable.value);
      } else {
        void upsertVariable(craftspaceId, id, null, "icon", variable.name, variable.value).then(
          (resolvedId) => {
            if (!resolvedId || resolvedId === id) return;
            set((current) => {
              const nextVars = current.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
              return {
                craftspaceVariables: {
                  ...current.craftspaceVariables,
                  [craftspaceId]: {
                    ...nextVars,
                    icons: nextVars.icons.map((i) => (i.id === id ? { ...i, id: resolvedId } : i)),
                  },
                },
              };
            });
          },
        );
      }
      return {
        craftspaceVariables: {
          ...state.craftspaceVariables,
          [craftspaceId]: { ...wsVars, icons: [...wsVars.icons, { ...variable, id }] },
        },
      };
    }),

  updateCraftspaceIconVariable: (craftspaceId: string, id: string, updates: Partial<Omit<IconVariable, "id">>) =>
    set((state) => {
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      const updated = { ...wsVars, icons: wsVars.icons.map((i) => (i.id === id ? { ...i, ...updates } : i)) };
      const icon = updated.icons.find((i) => i.id === id);
      if (icon) void upsertVariable(craftspaceId, id, null, "icon", icon.name, icon.value);
      return { craftspaceVariables: { ...state.craftspaceVariables, [craftspaceId]: updated } };
    }),

  deleteCraftspaceIconVariable: (craftspaceId: string, id: string) =>
    set((state) => {
      const wsVars = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
      void deleteVariable(craftspaceId, id);
      return {
        craftspaceVariables: {
          ...state.craftspaceVariables,
          [craftspaceId]: { ...wsVars, icons: wsVars.icons.filter((i) => i.id !== id) },
        },
      };
    }),

  getCraftspaceVariables: (craftspaceId: string) => {
    const vars = get().craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
    return { ...vars, angles: vars.angles ?? [] };
  },

  getCraftspaceColorVariable: (craftspaceId: string, id: string) =>
    (get().craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables).colors.find((c) => c.id === id),

  getCraftspaceSizeVariable: (craftspaceId: string, id: string) =>
    (get().craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables).sizes.find((s) => s.id === id),

  getCraftspaceAngleVariable: (craftspaceId: string, id: string) =>
    (get().craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables).angles.find((a) => a.id === id),

  getCraftspaceFontFamilyVariable: (craftspaceId: string, id: string) =>
    (get().craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables).fontFamilies.find((f) => f.id === id),

  getCraftspaceIconVariable: (craftspaceId: string, id: string) =>
    (get().craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables).icons.find((i) => i.id === id),

  // ─── Combined getters ─────────────────────────────────────────────────────

  getCombinedVariables: (craftspaceId: string, projectId: string) => {
    const state = get();
    const wsVarsRaw = state.craftspaceVariables[craftspaceId] ?? defaultCraftspaceVariables;
    const projVarsRaw = state.projectVariables[projectId] ?? defaultProjectVariables;
    const wsVars = { ...wsVarsRaw, angles: wsVarsRaw.angles ?? [] };
    const projVars = { ...projVarsRaw, angles: projVarsRaw.angles ?? [] };

    const mergeByName = <T extends { name: string }>(base: T[], overrides: T[]): T[] => {
      const result = [...base];
      for (const item of overrides) {
        const idx = result.findIndex((x) => x.name === item.name);
        if (idx >= 0) result[idx] = item;
        else result.push(item);
      }
      return result;
    };

    return {
      colors: mergeByName(wsVars.colors, projVars.colors),
      sizes: mergeByName(wsVars.sizes, projVars.sizes),
      angles: mergeByName(wsVars.angles, projVars.angles),
      fontFamilies: mergeByName(wsVars.fontFamilies, projVars.fontFamilies),
      icons: mergeByName(wsVars.icons, projVars.icons),
    };
  },

  updateCombinedCSSVariables: (craftspaceId: string, projectId: string) => {
    const combinedVars = get().getCombinedVariables(craftspaceId, projectId);
    const cssVariables = generateCSSVariables(
      combinedVars.colors,
      combinedVars.sizes,
      combinedVars.fontFamilies,
    );
    injectCSSVariables(cssVariables, projectId);
  },

}));
